import { localParts } from '@adop/shared';
import { lastNDates } from './format';

export interface DailyStat {
  date: string;
  orders: number;
  revenueCents: number;
}

export interface StatOrder {
  id?: string;
  created_at: string;
  status: string;
  total_cents: number;
}

export interface StatOrderItem {
  order_id: string;
  name: string;
  quantity: number;
  line_total_cents: number;
}

export interface TopItem {
  name: string;
  quantity: number;
  revenueCents: number;
}

const EXCLUDED_STATUSES = new Set(['cancelled', 'refunded']);

/** Orders and revenue per local day for the trailing `days` window. */
export function computeDailyStats(
  orders: StatOrder[],
  timezone: string,
  days: number,
  now: Date = new Date(),
): DailyStat[] {
  const dates = lastNDates(days, now);
  const buckets = new Map<string, DailyStat>(
    dates.map((date) => [date, { date, orders: 0, revenueCents: 0 }]),
  );
  for (const order of orders) {
    if (EXCLUDED_STATUSES.has(order.status)) continue;
    const date = localParts(new Date(order.created_at), timezone).date;
    const bucket = buckets.get(date);
    if (!bucket) continue;
    bucket.orders += 1;
    bucket.revenueCents += order.total_cents;
  }
  return dates.map((date) => buckets.get(date) as DailyStat);
}

/** Best-selling items by quantity for orders in the trailing window. */
export function computeTopItems(
  items: StatOrderItem[],
  orders: StatOrder[],
  timezone: string,
  days: number,
  now: Date = new Date(),
): TopItem[] {
  const validDates = new Set(lastNDates(days, now));
  const validOrders = new Set(
    orders
      .filter((order) => !EXCLUDED_STATUSES.has(order.status))
      .filter((order) => validDates.has(localParts(new Date(order.created_at), timezone).date))
      .map((order) => order.id)
      .filter((id): id is string => typeof id === 'string'),
  );

  // No orders inside the window means no items to rank, even if the caller
  // fetched order lines from outside it.
  if (validOrders.size === 0) return [];

  const aggregate = new Map<string, TopItem>();
  for (const item of items) {
    // Only count lines that belong to an in-window, non-cancelled order.
    if (!validOrders.has(item.order_id)) continue;
    const entry = aggregate.get(item.name) ?? { name: item.name, quantity: 0, revenueCents: 0 };
    entry.quantity += item.quantity;
    entry.revenueCents += item.line_total_cents;
    aggregate.set(item.name, entry);
  }
  return [...aggregate.values()].sort((a, b) => b.quantity - a.quantity).slice(0, 10);
}
